import { useId } from 'react';
import type { ReactElement } from 'react';
import { cn } from './cn';

export interface ToggleProps {
  checked: boolean;
  onChange: (checked: boolean) => void;
  label: string;
  description?: string;
  disabled?: boolean;
  id?: string;
  className?: string;
}

/**
 * Switch built on a real `<button>`, so Space and Enter come from the platform
 * and no key handling of our own is needed.
 */
export function Toggle({
  checked,
  onChange,
  label,
  description,
  disabled = false,
  id,
  className,
}: ToggleProps): ReactElement {
  const generatedId = useId();
  const switchId = id ?? generatedId;
  const labelId = `${switchId}-label`;
  const descriptionId = `${switchId}-description`;

  return (
    <div className={cn('flex items-start justify-between gap-4', className)}>
      <div className="min-w-0">
        <span id={labelId} className="block text-[13px] font-medium text-ink">
          {label}
        </span>
        {description && (
          <span id={descriptionId} className="mt-0.5 block text-xs leading-4 text-ink-muted">
            {description}
          </span>
        )}
      </div>

      <button
        type="button"
        role="switch"
        id={switchId}
        aria-checked={checked}
        aria-labelledby={labelId}
        aria-describedby={description ? descriptionId : undefined}
        aria-disabled={disabled || undefined}
        onClick={() => {
          if (disabled) return;
          onChange(!checked);
        }}
        className={cn(
          'relative mt-0.5 inline-flex h-6 w-10 shrink-0 items-center rounded-full border',
          'transition-colors duration-150 motion-reduce:transition-none',
          checked ? 'border-transparent bg-accent' : 'border-border bg-surface-sunken',
          disabled && 'cursor-not-allowed opacity-55',
        )}
      >
        <span
          aria-hidden="true"
          className={cn(
            'pointer-events-none block h-4 w-4 rounded-full bg-surface shadow-panel',
            'transition-transform duration-150 motion-reduce:transition-none',
            checked ? 'translate-x-[1.375rem]' : 'translate-x-[0.1875rem]',
          )}
        />
      </button>
    </div>
  );
}
